import { useCallback, useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { invalidateInboxStatusCache, type InboxProviderId } from '@/services/inboxSync';
import { useInboxConnectionsStore } from '@/state/inboxConnectionsStore';
import {
  clearInboxOauthMarkers,
  getInboxOauthPending,
  getInboxOauthSuccess,
} from '@/utils/inboxOauth';
import { pollInboxStatusUntilConnected } from '@/utils/inboxOauthResume';
import { logInboxSyncDebug } from '@/utils/inboxSyncDebug';

type InboxStatusResult = { connected?: boolean } | null | undefined;

type UseInboxOauthResumeOptions = {
  userId?: string;
  enabled?: boolean;
  checkStatus: (provider: InboxProviderId) => Promise<InboxStatusResult>;
  onConnected: (provider: InboxProviderId) => Promise<void> | void;
  canResume?: () => boolean;
  /**
   * Called when a pending/success OAuth marker exists but inbox status never shows connected
   * within the poll window (tokens may still be propagating). Use this to dismiss blocking UI.
   */
  onResumePollFinishedWithoutConnect?: (provider: InboxProviderId) => void;
};

const OAUTH_RESUME_POLL_OPTIONS = {
  maxAttempts: 14,
  baseDelayMs: 900,
  stepDelayMs: 450,
  maxDelayMs: 3500,
} as const;

export function useInboxOauthResume({
  userId,
  enabled = true,
  checkStatus,
  onConnected,
  canResume,
  onResumePollFinishedWithoutConnect,
}: UseInboxOauthResumeOptions): void {
  const runningRef = useRef(false);

  const run = useCallback(async () => {
    if (!enabled) return;
    if (canResume && !canResume()) return;
    if (runningRef.current) return;

    const effectiveUserId = userId ?? auth.currentUser?.uid;
    if (!effectiveUserId) return;

    logInboxSyncDebug('resume.run.started', { userId: effectiveUserId, enabled });
    runningRef.current = true;
    try {
      const success = await getInboxOauthSuccess();
      const pending = success ? null : await getInboxOauthPending();
      const provider = (success?.provider || pending?.provider) as InboxProviderId | undefined;
      if (!provider) {
        logInboxSyncDebug('resume.run.no_oauth_marker');
        return;
      }

      logInboxSyncDebug('resume.run.marker_found', {
        provider,
        source: success ? 'success' : 'pending',
      });

      const connected = await pollInboxStatusUntilConnected(
        provider,
        checkStatus,
        OAUTH_RESUME_POLL_OPTIONS,
      );
      if (!connected) {
        logInboxSyncDebug('resume.run.not_connected_after_poll', { provider });
        onResumePollFinishedWithoutConnect?.(provider);
        return;
      }

      invalidateInboxStatusCache();
      await useInboxConnectionsStore.getState().refreshFresh();
      await clearInboxOauthMarkers();
      logInboxSyncDebug('resume.run.connected', { provider });
      await onConnected(provider);
      logInboxSyncDebug('resume.run.on_connected_complete', { provider });
    } finally {
      runningRef.current = false;
      logInboxSyncDebug('resume.run.finished');
    }
  }, [
    userId,
    enabled,
    canResume,
    checkStatus,
    onConnected,
    onResumePollFinishedWithoutConnect,
  ]);

  useEffect(() => {
    if (!enabled) return;
    if (canResume && !canResume()) return;

    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') void run();
    });
    const unsubAuth = onAuthStateChanged(auth, () => {
      void run();
    });
    void run();
    return () => {
      sub.remove();
      unsubAuth();
    };
  }, [enabled, canResume, run]);
}
